import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';
import { APIError } from 'models';
import { SoftwareFilter, SoftwareFilterAttribute } from 'models/software';
import {
  AssignSoftwareRequest,
  SelectableSoftwareSuccess,
  SoftwareUpdate,
  SoftwareUpdateState,
  SoftwareUpdatesSuccess,
} from 'models/softwareUpate';
import {
  assignSoftware,
  getSoftwareUpdateHistory,
} from 'services/connectivityService/connectivityService';
import { findSoftwares } from 'services/softwareService/softwareService';

export const defaultState: SoftwareUpdateState = {
  selectableSoftware: {
    softwares: null,
    isLoading: false,
    error: null,
  },
  softwareUpdatesData: {
    softwareUpdates: [],
    isLoading: false,
    error: null,
    totalCount: 0,
    lastEvaluatedId: null,
    nextOffset: 0,
  },
};

// Export slice
export const softwareUpdateSlice = createSlice({
  name: 'softwareUpdate',
  initialState: defaultState,
  reducers: {
    getSelectableSoftwares: (state) => {
      state.selectableSoftware.isLoading = true;
      state.selectableSoftware.error = null;
    },
    getSelectableSoftwaresSuccess: (
      state,
      action: PayloadAction<SelectableSoftwareSuccess>
    ) => {
      state.selectableSoftware.isLoading = false;
      state.selectableSoftware.error = null;
      state.selectableSoftware.softwares = action.payload.response;
    },
    getSelectableSoftwaresFailure: (state, action: PayloadAction<APIError>) => {
      state.selectableSoftware.softwares = [];
      state.selectableSoftware.isLoading = false;
      state.selectableSoftware.error = action.payload;
    },
    getSoftwareUpdates: (state) => {
      state.softwareUpdatesData.isLoading = true;
      state.softwareUpdatesData.error = null;
      state.softwareUpdatesData.softwareUpdates = [];
    },
    getSoftwareUpdatesSuccess: (
      state,
      action: PayloadAction<SoftwareUpdatesSuccess>
    ) => {
      state.softwareUpdatesData.isLoading = false;
      state.softwareUpdatesData.softwareUpdates =
        action.payload.softwareUpdates;
      state.softwareUpdatesData.totalCount =
        action.payload.totalSoftwareUpdates;
      state.softwareUpdatesData.lastEvaluatedId =
        action.payload.lastEvaluatedId || null;
      state.softwareUpdatesData.error = null;
      state.softwareUpdatesData.nextOffset = action.payload.nextOffset;
    },
    getSoftwareUpdatesFailure: (state, action: PayloadAction<APIError>) => {
      state.softwareUpdatesData.softwareUpdates = [];
      state.softwareUpdatesData.isLoading = false;
      state.softwareUpdatesData.error = action.payload;
      state.softwareUpdatesData.lastEvaluatedId = null;
      state.softwareUpdatesData.totalCount = 0;
      state.softwareUpdatesData.nextOffset = 0;
    },
  },
});

// Export actions
export const {
  getSelectableSoftwares,
  getSelectableSoftwaresSuccess,
  getSelectableSoftwaresFailure,
  getSoftwareUpdates,
  getSoftwareUpdatesSuccess,
  getSoftwareUpdatesFailure,
} = softwareUpdateSlice.actions;

// Export reducer
export const softwareUpdateReducer = softwareUpdateSlice.reducer;

export const selectableSoftwaresSelector = (state: any) =>
  state.softwareUpdate.selectableSoftware;

export const softwareUpdateSelector = (state: any) =>
  state.softwareUpdate.softwareUpdatesData;

// Export thunk
export const fetchSelectableSoftwares =
  /* prettier-ignore */
  (softwareName: string) => async (dispatch: Dispatch) => {
    dispatch(getSelectableSoftwares());
    try {
      const limit = 10;
      const filter: SoftwareFilter = {
        attribute: SoftwareFilterAttribute.NAME,
        value: softwareName
      };
      const response = await findSoftwares(0, limit, [filter]);
      const selectableSoftwares = response.data.map((item) => ({ key: item.id, value: item.id, label: `${item.attributes.name} ${item.attributes.version}` }))
      dispatch(
        getSelectableSoftwaresSuccess({
          response: selectableSoftwares,
        })
      );
    } catch (e: any) {
      dispatch(getSelectableSoftwaresFailure(e));
    }
  };

export const clearSelectableSoftwares = () => async (dispatch: Dispatch) => {
  try {
    dispatch(
      getSelectableSoftwaresSuccess({
        response: [],
      })
    );
  } catch (e: any) {
    dispatch(getSelectableSoftwaresFailure(e));
  }
};

export const fetchSoftwareUpdates =
  /* prettier-ignore */
  (iprId: string, offset: number, startId?: string) => async (dispatch: Dispatch) => {
    dispatch(getSoftwareUpdates());
    try {
      const softwareUpdateResponse = await getSoftwareUpdateHistory(
        iprId,
        offset,
        5,
        startId
      );
      dispatch(
        getSoftwareUpdatesSuccess({
          softwareUpdates: softwareUpdateResponse.data,
          totalSoftwareUpdates: softwareUpdateResponse.meta.totalCount,
          lastEvaluatedId: softwareUpdateResponse.meta.lastEvaluatedId,
          nextOffset: softwareUpdateResponse.meta.nextOffset || 0,
        })
      );
    } catch (e: APIError | any) {
      console.log('INSIDE ERROR SOFTWARE_UPDATE FAILURE' + e);
      dispatch(getSoftwareUpdatesFailure(e));
    }
  };

export const fetchSoftwareUpdatesFromPrevPage =
  /* prettier-ignore */
  (data: SoftwareUpdate[], totalCount: number, lastEvaluatedId: string, nextOffset?: number) => async (dispatch: Dispatch) => {
    dispatch(getSoftwareUpdates());
    try {
      dispatch(
        getSoftwareUpdatesSuccess({
          softwareUpdates: data,
          totalSoftwareUpdates: totalCount,
          lastEvaluatedId: lastEvaluatedId,
          nextOffset: nextOffset || 0,
        })
      );
    } catch (e: APIError | any) {
      dispatch(getSoftwareUpdatesFailure(e));
    }
  };

export const assignSoftwareToDevice =
  (iprId: string, softwareId: string) => async (dispatch: Dispatch) => {
    dispatch(getSoftwareUpdates());
    try {
      const assignSoftwareRequest: AssignSoftwareRequest = {
        data: {
          type: 'softwareupdate',
          attributes: {
            softwareId,
          },
        },
      };
      const newSoftwareUpdate = await assignSoftware(
        iprId,
        assignSoftwareRequest
      );
      console.log(
        `New softwareUpdateId: ${JSON.stringify(newSoftwareUpdate.data)}`
      );
      const softwareUpdateResponse = await getSoftwareUpdateHistory(
        iprId,
        0,
        5
      );
      dispatch(
        getSoftwareUpdatesSuccess({
          softwareUpdates: softwareUpdateResponse.data,
          totalSoftwareUpdates: softwareUpdateResponse.meta.totalCount,
          lastEvaluatedId: softwareUpdateResponse.meta.lastEvaluatedId,
          nextOffset: softwareUpdateResponse.meta.nextOffset || 0,
        })
      );
    } catch (e: APIError | any) {
      dispatch(getSoftwareUpdatesFailure(e.errors[0] || e));
    }
  };
